import React, { useMemo } from 'react'
import { Card, Table, Badge, Select } from 'antd'
import { PageHeader, CommonFilters } from '@components'
import { getAIQualityCheckResultList } from '@services/task'
import { formatTime, getUrlParam, notEmptyArray } from '@library'
import { FieldItem } from 'src/components/CommonFilters/configs'
import { useTableService } from '@hooks/useTableService'
import { antdSortMap } from '@configs/map'
import { useTableColumn, TableColumnProps } from '@hooks'
import { getTableXwidth, TableIdEnum, getSortOrder } from '@configs/table'
import { TableProps } from 'antd/lib/table'
import { AIQualityCheckTaskDetailItem, AIQualityCheckTaskDetailFilter } from 'src/types/ai-quality-check'
import { Link, useParams } from 'react-router-dom'

const CHECK_STATUS = {
  COMPLETED: 1,
  UNCOMPLETED: 0,
}

const RUN_STATUS = {
  NOT_EXECUTED: 0,
  RUNNING: 1,
  EXECUTION_SUCCESS: 2,
  EXECUTION_FAILURE: 3,
}

const runStatusTextMap = {
  [RUN_STATUS.NOT_EXECUTED]: '未执行',
  [RUN_STATUS.RUNNING]: '执行中',
  [RUN_STATUS.EXECUTION_SUCCESS]: '执行成功',
  [RUN_STATUS.EXECUTION_FAILURE]: '执行失败',
}

const runStatusColorMap = {
  [RUN_STATUS.NOT_EXECUTED]: 'grey',
  [RUN_STATUS.RUNNING]: 'orange',
  [RUN_STATUS.EXECUTION_SUCCESS]: 'green',
  [RUN_STATUS.EXECUTION_FAILURE]: 'red',
}

const taskFilterItems: FieldItem[] = [
  {
    fieldName: 'recordIds',
    label: 'recordId',
    type: 'multiTags',
    handleValue: val => ({
      recordIds: val?.length ? val?.map(item => item ? +item : item) : undefined,
    }),
  },
  {
    fieldName: 'runStatus',
    label: '执行状态',
    render: () => {
      const options = [
        { label: '未执行', value: RUN_STATUS.NOT_EXECUTED },
        { label: '执行中', value: RUN_STATUS.RUNNING },
        { label: '执行成功', value: RUN_STATUS.EXECUTION_SUCCESS },
        { label: '执行失败', value: RUN_STATUS.EXECUTION_FAILURE },
      ]
      return (<Select allowClear options={options} />)
    },
  },
  {
    fieldName: 'status',
    label: '复核状态',
    render: () => {
      const options = [
        { label: '待复核', value: CHECK_STATUS.UNCOMPLETED },
        { label: '已复核', value: CHECK_STATUS.COMPLETED },
      ]
      return (<Select placeholder="全部" allowClear options={options} />)
    },
  },
  {
    fieldName: 'customerName',
    label: '客户名称',
    type: 'customerNameSelect',
  },
  {
    fieldName: 'cid',
    label: 'cid',
    type: 'multiTags',
  },
  {
    fieldName: 'salesName',
    label: '跟进人',
    type: 'staffSelectNull',
    handleValue: (val: string[]) => ({
      salesName: notEmptyArray(val) ? val?.map(v => v.replace('identifier', '')) : undefined,
    }),
  },
  {
    fieldName: 'createTime',
    label: '任务时间',
    type: 'rangePicker',
    handleValue: val => ({
      createTimeFrom: formatTime(val?.[0], 'start'),
      createTimeTo: formatTime(val?.[1], 'end'),
    }),
  },
  {
    fieldName: 'callTime',
    label: '通话时间',
    type: 'rangePicker',
    handleValue: val => ({
      callBeginTimeFrom: formatTime(val?.[0], 'start'),
      callBeginTimeTo: formatTime(val?.[1], 'end'),
    }),
  },
]

const QualityCheckTaskDetailPage = () => {
  const taskName = getUrlParam('taskName')
  const updateTime = getUrlParam('updateTime')
  const id = +useParams<{ id: string }>()?.id
  const initFilter: AIQualityCheckTaskDetailFilter = {
    page: 1,
    pageSize: 10,
    taskIds: [id],
  }

  const {
    loading,
    list: taskDetailList,
    filter,
    pagination,
    setFilter,
    resetFilter,
  } = useTableService(getAIQualityCheckResultList, initFilter, '获取质检任务详情列表失败')

  const columns: TableColumnProps<AIQualityCheckTaskDetailItem>[] = useMemo(() => [
    {
      title: '任务时间',
      dataIndex: 'createTime',
      width: 120,
    },
    {
      title: 'recordid',
      dataIndex: 'recordId',
      width: 80,
      render: (recordId, record) => (
        <Link target="_blank" to={`/task/quality-check/reexamine-detail/${record.id}?recordId=${recordId}`}>
          {recordId}
        </Link>
      ),
    },
    {
      title: 'cid',
      dataIndex: 'cid',
      width: 300,
    },
    {
      title: '客户名称',
      dataIndex: 'customerName',
      width: 120,
    },
    {
      title: '跟进人',
      dataIndex: 'salesName',
      width: 100,
    },
    {
      title: '通话时间',
      dataIndex: 'callBeginTime',
      width: 200,
      sorter: true,
      sortOrder: filter.sort === 'callBeginTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '执行状态',
      dataIndex: 'runStatus',
      width: 120,
      render: (status: number, record) => {
        return <Badge key={record.recordId} color={runStatusColorMap[status]} text={runStatusTextMap[status]} />
      },
    },
    {
      title: '复核状态',
      dataIndex: 'status',
      width: 100,
      render: (status: number, record) => {
        const text = status === CHECK_STATUS.COMPLETED ? '已复核' : '待复核'
        const color = status === CHECK_STATUS.COMPLETED ? 'green' : 'red'
        return <Badge key={record.recordId} color={color} text={text} />
      },
    },
    {
      title: '命中规则数',
      dataIndex: 'aiHitRules',
      width: 150,
      sorter: true,
      sortOrder: filter.sort === 'aiHitRules' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '实际违规数',
      dataIndex: 'reviewHitRules',
      width: 150,
      sorter: true,
      sortOrder: filter.sort === 'reviewHitRules' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '模型评分',
      dataIndex: 'aiScore',
      width: 120,
      sorter: true,
      sortOrder: filter.sort === 'aiScore' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '复核总分',
      dataIndex: 'reviewScore',
      width: 120,
      sorter: true,
      sortOrder: filter.sort === 'reviewScore' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 100,
      render: (action, record) => (
        record.runStatus === RUN_STATUS.EXECUTION_SUCCESS ? (
          <Link target="_blank" to={`/task/quality-check/reexamine-detail/${record.id}?recordId=${record.recordId}`}>
            复核
          </Link>
        ) : (
          <span style={{ color: '#C7C7C7' }}>复核</span>
        )
      ),
    },
  ], [filter])

  const [, columnProps] = useTableColumn(TableIdEnum.QUALITY_CHECK_TASK_DETAIL, columns)
  const tableXWidth = useMemo(() => getTableXwidth(columnProps), [columnProps])

  const onTableChange: TableProps<AIQualityCheckTaskDetailItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<AIQualityCheckTaskDetailFilter>(sorter)
    setFilter(sort)
  }

  return (
    <>
      <PageHeader title={taskName} subTitle={updateTime} />
      <Card className="mt20 mb20">
        <CommonFilters
          className="custom-Label mt20 mb20"
          itemList={taskFilterItems}
          formLayout={{
            labelCol: { span: 6 },
            wrapperCol: { span: 18 },
          }}
          standstill={8}
          onSubmit={filter => setFilter({ ...filter, page: 1 })}
          onReset={() => resetFilter(initFilter)}
        />
      </Card>
      <Card className="mt20">
        <Table
          className="mt20"
          style={{ maxWidth: tableXWidth + 'px' }}
          scroll={{ x: tableXWidth }}
          loading={loading}
          columns={columns}
          dataSource={taskDetailList}
          pagination={pagination}
          onChange={onTableChange}
        />
      </Card>
    </>
  )
}

export default QualityCheckTaskDetailPage
